<template>
  <div
      v-if="show || fadeOut || fadeIn"
      :class="classes"
      class="d-print-none"
  >
    <div class="container-fluid">
      <ClickWrapper
          class="io-modal__container"
          ref="root"
          @outside="onClickOutside"
      >
        <div
            class="app-modal__card"
            :class="{
              plain,
            }"
        >
          <div
              v-if="loading"
              class="app-modal__loader"
          >
            <div>
              <FontAwesomeIcon icon="fa-light fa-spinner" class="fa-pulse"/>
            </div>
          </div>
          <BaseGridRow>
            <BaseGridCol
                v-if="headline"
                class="app-modal__card__headline"
            >
              <div v-html="headline" />
            </BaseGridCol>
            <BaseGridCol>
              <slot></slot>
            </BaseGridCol>
          </BaseGridRow>
          <button
              v-if="!hideClose"
              class="close close-absolute font-h4"
              @click="close"
          >
            <FontAwesomeIcon icon="fa-regular fa-xmark"/>
          </button>
        </div>
      </ClickWrapper>
    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseGridCol from '@/components/BaseGridCol.vue';
import {
  ref,
  computed,
} from 'vue';
import ClickWrapper from '@/components/ClickWrapper.vue';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSpinner } from '@fortawesome/pro-light-svg-icons';
import { faXmark } from '@fortawesome/pro-regular-svg-icons';

library.add(faSpinner, faXmark);

const emit = defineEmits(['close']);

const props = withDefaults(defineProps<{
  headline?: string
  closeable?: boolean
  small?: boolean
  large?: boolean
  loading?: boolean
  plain?: boolean
  hideClose?: boolean
}>(), {
  closeable: true,
});

const show = ref(false);
const fadeIn = ref(false);
const fadeOut = ref(false);

const classes = computed(() => ({
  'app-modal': true,
  open: fadeIn.value || show.value || fadeOut.value,
  shown: show.value,
  small: props.small,
  large: props.large,
}));

const open = () => {
  fadeIn.value = true;
  setTimeout(() => {
    show.value = true;
  }, 25);
  setTimeout(() => {
    fadeIn.value = false;
  }, 680);
};

const close = () => {
  fadeOut.value = true;
  show.value = false;
  setTimeout(() => {
    fadeOut.value = false;
  }, 680);

  if (props.closeable) {
    emit('close');
  }
};

const onClickOutside = () => {
  if (props.closeable) {
    close();
  }
};

defineExpose({
  open,
  close,
});
</script>

<style lang="scss">
@import 'src/scss/component-helpers';

.app-modal {
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 20000;
  background-color: transparentize(#000, 1);
  transition: background-color .68s ease-out;
  display: none;

  &.open {
    display: grid;
    grid-template-columns: 100%;
    justify-content: center;
    align-items: center;
  }

  &__container {
    max-width: calc(100% - (var(--container-fluid-padding-right) * 2));
  }

  &__card {
    --modal-card-padding-y: 60px;
    --modal-card-padding-x: 30px;
    width: 1110px;
    max-width: 100%;
    max-height: calc(100vh - (var(--container-fluid-padding-right) * 2));
    overflow: auto;
    margin: 0 auto;
    opacity: 0;
    transform: translateY(100vw) scale(.5);
    transition: opacity .68s ease-out, transform .68s ease-out;
    box-shadow: 0 3px 6px rgba(0, 0, 0, .15);
    background-color: #FFFFFF;
    border-radius: 20px;
    position: relative;
    padding: var(--modal-card-padding-y) var(--modal-card-padding-x);
    @include font-weight-fix;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      border-radius: 40px;
    }

    @media(min-width: map-get($grid-breakpoints, 'xl')) {
      --modal-card-padding-y: 80px;
      --modal-card-padding-x: 40px;
    }

    &.plain {
      padding: 0;
    }

    &__headline {
      font-size: 1.25rem;
      font-weight: 700;

      @media(min-width: map-get($grid-breakpoints, 'lg')) {
        font-size: 1.875rem;
      }
    }
  }

  &.large {
    .app-modal__card {
      width: 1680px;
    }
  }

  &.shown {
    background-color: transparentize(#000, .88);

    .app-modal__card {
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }

  &__loader {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 2;
    background: transparentize(#F7F7F7, .45);
    box-shadow: 0 0 6px inset rgba(0, 0, 0, .16);
    border-radius: 10px;

    > div {
      position: absolute;
      height: auto;
      width: auto;
      top: 50%;
      left: 50%;
      transform: translateY(-50%) translateX(-50%);
      @include responsive-size-xl(60);
      color: $body-color;
    }

    i, svg {
      color: #38383A;
    }
  }

  button.close {
    font-size: 1rem;
    border: 0;
    background: transparent;
    padding: 0;
    outline: 0;
    position: absolute;
    color: #38383A;
    top: 30px;
    right: 30px;

    @media(min-width: map-get($grid-breakpoints, 'lg')) {
      font-size: 1.5rem;
    }
  }
}
</style>
